import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Tag, Typography, Space, Button, Empty, App, Popconfirm,
  Tooltip, Tabs, Modal, Form, Input, Select, Alert, Row, Col,
} from 'antd';
import {
  EyeOutlined, CalendarOutlined, CheckOutlined, CloseOutlined, StopOutlined,
  PlusOutlined, SearchOutlined, TagOutlined,
} from '@ant-design/icons';
import PujaSpinner from '../../components/PujaSpinner';
import useColumnasResponsive from '../../components/tabla/useColumnasResponsive';
import { eventoService, clienteService, cabanaService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { fecha } from '../../utils/format';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Title, Text, Paragraph } = Typography;

/**
 * Las INVITACIONES, en las dos direcciones.
 *
 * POR QUÉ ESTÁN LAS DOS ACÁ. Antes se invitaba desde Cabañas y se seguía desde
 * esta pantalla, y eso confundía: dos lugares para una misma cosa. Peor todavía,
 * la invitación PENDIENTE no aparecía en ningún lado — la grilla de Cabañas
 * lista las cabañas de invitados ACTIVOS, así que una pendiente se quedaba sin
 * fila donde mostrarse. Justo la que más hay que ver, porque espera respuesta.
 *
 * La causa era conceptual: **una invitación no es una cabaña**. Es un permiso
 * entre dos EMPRESAS. Cabañas lista unidades productivas —nombres para el
 * catálogo, qué escribir en el CSV— y eso es otra cosa.
 *
 * Así que cada pantalla hace un solo trabajo:
 *   Cabañas       las unidades productivas
 *   Invitaciones  los permisos: quién puede ver qué, y hasta cuándo
 */

/** Qué métricas comparte el titular. El texto importa más que el código: si el
 *  invitado no entiende qué está aceptando, la decisión la sigue tomando uno solo. */
const NIVEL_ANALYTICS = {
  nada: { color: 'default', texto: 'No comparte métricas' },
  agregado: { color: 'blue', texto: 'Comparte el total del remate y lo tuyo' },
  total: { color: 'green', texto: 'Comparte todo el detalle del remate' },
};

const ESTADOS = {
  pendiente: { color: 'orange', recibida: 'Esperando tu respuesta', enviada: 'Esperando respuesta' },
  activa: { color: 'green', recibida: 'Aceptada', enviada: 'Aceptada' },
  rechazada: { color: 'default', recibida: 'La rechazaste', enviada: 'Rechazada' },
  revocada: { color: 'default', recibida: 'Revocada', enviada: 'Revocada' },
};

const Invitaciones = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { tieneRecurso } = useAuth();

  const [recibidas, setRecibidas] = useState([]);
  const [enviadas, setEnviadas] = useState([]);
  const [loading, setLoading] = useState(true);
  /* MIS unidades productivas, para elegir con cuáles participo al aceptar.
   * Vienen en la misma respuesta que las invitaciones —son las mismas para
   * todas— así que no hay una request extra. */
  const [misCabanas, setMisCabanas] = useState([]);
  const [modalAceptar, setModalAceptar] = useState(null);   // null | fila
  const [cabanasElegidas, setCabanasElegidas] = useState([]);
  const [aceptando, setAceptando] = useState(false);
  /* COMO SE PRESENTA cada una de mis cabañas: lo que se escribe en el CSV
   * (`cabana.alias`) y la marca publica (`cabana.nombre_publico`). Es el paso
   * 1 de un remate compartido: con el codigo el organizador arma la planilla
   * y dice de quien es cada animal; con la marca, el comprador sabe de quien
   * es en el micrositio, el catalogo y las caratulas. Hasta el 2026-10-26 la
   * invitada no tenia donde ponerlos —su plan es de solo lectura y Cabañas le
   * bloqueaba el lapiz— asi que ahora se piden al ACEPTAR y se pueden cambiar
   * despues desde la fila. Son por CABAÑA, no por remate: valen en todos los
   * remates en los que participe. {id_cabana: {alias, nombre_publico}} */
  const [codigos, setCodigos] = useState({});
  const [modalCodigos, setModalCodigos] = useState(null);   // null | fila (ya aceptada)
  const [guardandoCodigos, setGuardandoCodigos] = useState(false);

  const puedeInvitar = tieneRecurso('multi_cabanas');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      try {
        const res = await eventoService.getMisInvitaciones();
        setRecibidas(res.data?.invitaciones || []);
        setMisCabanas(res.data?.mis_cabanas || []);
      } catch (error) {
        message.error(error.response?.data?.error || 'No pudimos cargar tus invitaciones');
      }
      // Las enviadas sólo las tiene quien organiza remates compartidos, por eso
      // se piden aparte y detrás del guard.
      if (puedeInvitar) {
        try {
          const res = await eventoService.getInvitacionesEnviadas();
          setEnviadas(res.data?.invitaciones || []);
        } catch (error) {
          /* CON CARTEL, y no en silencio.
           *
           * El caso "no tiene el recurso" ya lo cubre el `if (puedeInvitar)`:
           * acá no puede llegar un 403 por plan. Lo único que llega es un 500,
           * un timeout o la red caída — fallas reales.
           *
           * Y con la lista vacía la solapa Enviadas renderiza "Todavía no
           * invitaste a ninguna cabaña a tus remates", que es MENTIRA para
           * alguien que invitó cinco, y encima se queda sin el botón de
           * revocar. Eso no es degradación silenciosa: es información falsa. */
          setEnviadas([]);
          message.error(error.response?.data?.error
                        || 'No pudimos cargar las invitaciones que enviaste');
        }
      }
    } finally {
      // En `finally`: son dos requests y la segunda es condicional, así que un
      // throw inesperado dejaba la pantalla en spinner para siempre.
      setLoading(false);
    }
  }, [message, puedeInvitar]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Del lado del INVITADO ────────────────────────────────────────────────
  const responder = async (fila, respuesta, cabanas) => {
    try {
      await eventoService.responderInvitacion(fila.id_evento_invitado, respuesta, cabanas);
      message.success(respuesta === 'acepto' ? 'Invitación aceptada' : 'Invitación rechazada');
      cargar();
      return true;
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos registrar tu respuesta');
      return false;
    }
  };

  const codigosActuales = () => Object.fromEntries(
    misCabanas.map((c) => [c.id_cabana, { alias: c.alias || '', nombre_publico: c.nombre_publico || '' }]));

  const cambioDe = (id_cabana, campo, valor) => setCodigos((prev) => ({
    ...prev, [id_cabana]: { ...(prev[id_cabana] || {}), [campo]: valor },
  }));

  /* Guarda SOLO lo que cambio, cabaña por cabaña y campo por campo. Un error
   * en una no frena a las demas: cada dato es independiente, y el mensaje dice
   * cual fallo. Devuelve cuantas cabañas se guardaron. */
  const guardarCodigos = async () => {
    const antes = codigosActuales();
    let guardados = 0;
    for (const c of misCabanas) {
      const ahora = codigos[c.id_cabana] || {};
      const cambios = {};
      ['alias', 'nombre_publico'].forEach((campo) => {
        if ((ahora[campo] ?? '').trim() !== antes[c.id_cabana][campo]) cambios[campo] = ahora[campo] ?? '';
      });
      if (!Object.keys(cambios).length) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await cabanaService.editarPresentacion(c.id_cabana, cambios);
        guardados += 1;
      } catch (error) {
        message.error(`${c.nom_cabana}: ${error.response?.data?.error || 'no pudimos guardar los cambios'}`);
      }
    }
    if (guardados) message.success(guardados === 1 ? 'Cabaña actualizada' : `${guardados} cabañas actualizadas`);
    return guardados;
  };

  /* Abre el modal de aceptar. SIEMPRE, desde el 2026-10-26: antes con una sola
   * cabaña se aceptaba derecho desde un Popconfirm, porque "¿con cuál
   * participás?" tenía una única respuesta. Ahora el modal también pide cómo
   * se presenta cada cabaña, que hace falta igual con una cabaña que con cinco.
   * El selector de cabañas sigue apareciendo sólo con más de una. */
  const abrirAceptar = (fila) => {
    setCabanasElegidas([]);
    setCodigos(codigosActuales());
    setModalAceptar(fila);
  };

  const confirmarAceptar = async () => {
    setAceptando(true);
    try {
      /* Sin nada tildado se manda `undefined`, o sea "participo con todas". Es
       * la misma regla que en el backend: la ausencia de restricción no es una
       * restricción vacía. */
      const ok = await responder(modalAceptar, 'acepto',
                                 cabanasElegidas.length ? cabanasElegidas : undefined);
      if (ok) {
        // Primero aceptar, después los códigos: son dos cosas, y si el código
        // falla la invitación ya quedó aceptada, que es lo que importa.
        if (await guardarCodigos()) cargar();
        setModalAceptar(null);
      }
    } finally {
      // En `finally` y no suelto: hoy `responder` traga todo y devuelve un
      // booleano, pero la regla existe para sobrevivir al proximo refactor.
      setAceptando(false);
    }
  };

  const abrirCodigos = (fila) => {
    setCodigos(codigosActuales());
    setModalCodigos(fila);
  };

  const confirmarCodigos = async () => {
    setGuardandoCodigos(true);
    try {
      if (await guardarCodigos()) cargar();
      setModalCodigos(null);
    } finally {
      setGuardandoCodigos(false);
    }
  };

  /* Mis cabañas con sus dos campos, compartido por los dos modales. */
  const camposCodigos = (
    <div style={{ marginTop: 16 }}>
      <Text strong>¿Cómo aparece cada cabaña tuya?</Text>
      <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 8 }}>
        <strong>En el CSV</strong> es el código corto de la columna CABAÑA de la carga
        masiva (el nombre completo matchea igual). <strong>Marca pública</strong> es lo que
        ve el comprador en el micrositio, el catálogo y las carátulas. Los dos son
        opcionales y valen en todos los remates en los que participes. El <strong>logo</strong>,
        que firma la ficha de cada animal en el catálogo y las carátulas, se carga en
        Cabañas, con el botón «Cómo aparece».
      </Paragraph>
      {misCabanas.map((c) => (
        <div key={c.id_cabana} style={{ marginBottom: 12 }}>
          <Text strong>{c.nom_cabana}</Text>
          <Row gutter={8} style={{ marginTop: 4 }}>
            <Col xs={24} sm={10}>
              <Input
                size="small"
                maxLength={30}
                addonBefore="En el CSV"
                placeholder={c.nom_cabana}
                value={codigos[c.id_cabana]?.alias ?? ''}
                onChange={(e) => cambioDe(c.id_cabana, 'alias', e.target.value)}
              />
            </Col>
            <Col xs={24} sm={14}>
              <Input
                size="small"
                maxLength={255}
                addonBefore="Marca"
                placeholder="Automática"
                value={codigos[c.id_cabana]?.nombre_publico ?? ''}
                onChange={(e) => cambioDe(c.id_cabana, 'nombre_publico', e.target.value)}
              />
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );

  const revocarMia = async (fila) => {
    try {
      await eventoService.revocarMiInvitacion(fila.id_evento_invitado);
      message.success('Invitación revocada');
      cargar();
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos revocar la invitación');
    }
  };

  // ── Del lado del TITULAR ─────────────────────────────────────────────────
  const revocarEnviada = async (fila) => {
    try {
      await eventoService.revocarInvitado(fila.id_evento, fila.id_clientes_invitado);
      message.success('Invitación revocada');
      cargar();
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos revocar la invitación');
    }
  };

  const [modalAbierto, setModalAbierto] = useState(false);
  const [form] = Form.useForm();
  const [buscando, setBuscando] = useState(false);
  const [encontrada, setEncontrada] = useState(null);
  const [errorBusqueda, setErrorBusqueda] = useState(null);
  const [remates, setRemates] = useState([]);
  const [invitando, setInvitando] = useState(false);
  // Sin `loading` el dropdown de remates se abre VACIO mientras la request
  // viaja, y la base esta en otra region. Vive en el bloque del TITULAR
  // porque lo usa solo su modal.
  const [cargandoRemates, setCargandoRemates] = useState(false);

  const abrirModal = async () => {
    setModalAbierto(true);
    setEncontrada(null);
    setErrorBusqueda(null);
    form.resetFields();
    setCargandoRemates(true);
    try {
      const res = await eventoService.getListaCorta();
      setRemates(eventosDeListaCorta(res));
    } catch (error) {
      /* Va con `message.error` y no en silencio: sin remates el Select queda
         vacío, `id_evento` es obligatorio y la invitación NO SE PUEDE MANDAR.
         Cuando al fallar la persona pierde una función, no es una degradación
         aceptable — es un error y hay que decirlo. */
      setRemates([]);
      message.error(error.response?.data?.error || 'No pudimos cargar tus remates');
    } finally {
      setCargandoRemates(false);
    }
  };

  const buscarPorCuit = async () => {
    const cuit = form.getFieldValue('cuit');
    if (!cuit) return;
    setBuscando(true);
    setEncontrada(null);
    setErrorBusqueda(null);
    try {
      const res = await clienteService.buscarPorCuit(cuit);
      if (res.data.es_propio) {
        setErrorBusqueda('Ese CUIT es el tuyo. No podés invitarte a tu propio remate.');
      } else {
        setEncontrada(res.data);
      }
    } catch (error) {
      // El mensaje del CUIT no registrado dice QUÉ HACER, y no sólo que falló:
      // con el alta de cabañas centralizada en Puja, un "no existe" sin camino
      // a seguir deja al productor trabado sin saber a quién preguntarle.
      setErrorBusqueda(
        error.response?.data?.codigo === 'CABANA_NO_REGISTRADA'
          ? 'Esa cabaña todavía no es cliente de Puja. Escribinos para darla de alta y después la invitás.'
          : error.response?.data?.error || 'No pudimos buscar ese CUIT'
      );
    } finally {
      setBuscando(false);
    }
  };

  const invitar = async () => {
    /* `validateFields` va DENTRO del try: al fallar rechaza la promesa, y el
     * `onOk` del Modal de antd no la atrapa — queda una unhandled rejection en
     * consola. El usuario ve el error inline por las `rules`, así que no pierde
     * nada; lo que se evita es el ruido. */
    let valores;
    try {
      valores = await form.validateFields(['id_evento']);
    } catch {
      return;   // antd ya pintó el error en el campo
    }
    if (!encontrada) return;
    setInvitando(true);
    try {
      await eventoService.invitarCabana(valores.id_evento, encontrada.id_clientes);
      message.success(`Invitación enviada a ${encontrada.razon_social}`);
      setModalAbierto(false);
      cargar();
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos crear la invitación');
    } finally {
      setInvitando(false);
    }
  };

  // ── Columnas ─────────────────────────────────────────────────────────────
  const colsRecibidas = [
    {
      title: 'Remate',
      dataIndex: ['evento', 'nombre_evento'],
      key: 'nombre_evento',
      render: (valor, fila) => (
        <Space direction="vertical" size={0}>
          <Text strong>{valor || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fila.evento?.titular || 'Cabaña organizadora'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Fechas',
      dataIndex: ['evento', 'fecha_inicio'],
      key: 'fechas',
      render: (desde, fila) => `${fecha(desde)} — ${fecha(fila.evento?.fecha_fin)}`,
    },
    {
      // "en el remate" y no sólo "mis animales": el número es cuántos consigné
      // ACÁ, no cuántos tengo. Sin la aclaración, un 0 se lee como un error —
      // le pasó al usuario probando con una cabaña de 309 animales.
      title: 'Mis animales en el remate',
      dataIndex: 'animales_propios_en_el_evento',
      key: 'animales',
      align: 'center',
      render: (n) => <Tag color={n > 0 ? 'blue' : 'default'}>{n ?? 0}</Tag>,
    },
    {
      /* CON QUÉ PARTICIPO. Es la decisión que tomó el propio invitado al
         aceptar, y sin esta columna se vuelve invisible en el mismo momento en
         que la toma: el botón de aceptar desaparece al dejar de estar pendiente.
         Una decisión sobre el acceso a tus propios animales que sólo podés
         mirar una vez no sirve de nada.

         El titular ya ve su equivalente en la solapa Enviadas; el que decidió
         no veía nada. */
      title: 'Participo con',
      dataIndex: 'cabanas_elegidas',
      key: 'cabanas_elegidas',
      render: (elegidas, fila) => {
        if (!fila.activa) return <span style={{ color: '#999' }}>—</span>;
        // VACÍO ES "TODAS", NO "NINGUNA": es la regla de
        // `evento_invitado_cabana`, y mostrarlo como vacío asustaría de gusto.
        if (!elegidas?.length) return <Tag color="green">Todas mis cabañas</Tag>;
        /* El NOMBRE viene del backend, no se cruza contra `misCabanas`: esa
           lista filtra las dadas de baja, y una unidad de baja puede seguir
           participando de un remate ya arrancado. Cruzando ids, esa baja le
           saldría al usuario como un `#7`. */
        return (
          <Space size={4} wrap>
            {elegidas.map((c) => (
              <Tag key={c.id_cabana} color={c.activa ? 'blue' : 'default'}>
                {c.nom_cabana}{c.activa ? '' : ' (de baja)'}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Métricas',
      dataIndex: 'analytics_compartidas',
      key: 'analytics',
      render: (nivel) => {
        const cfg = NIVEL_ANALYTICS[nivel];
        return cfg ? <Tag color={cfg.color}>{cfg.texto}</Tag> : null;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const cfg = ESTADOS[estado] || { color: 'default', recibida: estado };
        return <Tag color={cfg.color}>{cfg.recibida}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      render: (_, fila) => (
        <Space size={4}>
          {/* ACEPTAR / RECHAZAR es lo que abre o cierra la pared: hasta que
              respondas, el organizador NO ve ni un animal tuyo. */}
          {fila.pendiente && (
            <>
              {/* Siempre el modal (ver `abrirAceptar`): además de con qué
                  cabañas participo, pide lo que se escribe en el CSV. */}
              <Tooltip title="Aceptar">
                <Button type="text" icon={<CheckOutlined />}
                        style={{ color: '#52c41a' }}
                        onClick={() => abrirAceptar(fila)} />
              </Tooltip>
              <Popconfirm
                title="¿Rechazar la invitación?"
                description="No se le va a dar ningún acceso a tus animales."
                okText="Rechazar" cancelText="Cancelar" okButtonProps={{ danger: true }}
                onConfirm={() => responder(fila, 'rechazo')}
              >
                <Tooltip title="Rechazar">
                  <Button type="text" danger icon={<CloseOutlined />} />
                </Tooltip>
              </Popconfirm>
            </>
          )}
          {fila.activa && (
            <Tooltip title="Cómo aparecen mis cabañas: código del CSV y marca pública">
              <Button type="text" icon={<TagOutlined />} onClick={() => abrirCodigos(fila)} />
            </Tooltip>
          )}
          {fila.activa && (
            <Popconfirm
              title="¿Revocar la invitación?"
              description={
                'La cabaña organizadora deja de ver y gestionar tus animales al '
                + 'instante. Los que ya cargaste siguen siendo tuyos.'
              }
              okText="Revocar" cancelText="Cancelar" okButtonProps={{ danger: true }}
              onConfirm={() => revocarMia(fila)}
            >
              <Tooltip title="Revocar">
                <Button type="text" danger icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
          <Tooltip title="Ver el remate">
            <Button type="text" icon={<EyeOutlined />} disabled={!fila.activa}
                    onClick={() => navigate(`/eventos/${fila.id_evento}`)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const colsEnviadas = [
    {
      title: 'Cabaña',
      dataIndex: 'invitada',
      key: 'invitada',
      render: (valor, fila) => (
        <Space direction="vertical" size={0}>
          <Text strong>{valor}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{fila.nombre_evento}</Text>
        </Space>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado_env',
      render: (estado) => {
        const cfg = ESTADOS[estado] || { color: 'default', enviada: estado };
        return <Tag color={cfg.color}>{cfg.enviada}</Tag>;
      },
    },
    {
      // Sólo si aceptó: antes de eso, sus unidades productivas no son asunto
      // del que invita. Es coherente con que la pendiente no abra nada.
      title: 'En el CSV escribí',
      dataIndex: 'cabanas',
      key: 'cabanas',
      render: (cabanas) => (cabanas?.length
        ? <Space size={4} wrap>{cabanas.map((c) => <Tag key={c} color="blue">{c}</Tag>)}</Space>
        : <Text type="secondary">—</Text>),
    },
    {
      title: 'Animales en mis remates',
      dataIndex: 'animales_en_mis_remates',
      key: 'animales_env',
      align: 'center',
      render: (n) => <Tag color={n > 0 ? 'blue' : 'default'}>{n ?? 0}</Tag>,
    },
    {
      title: 'Acciones',
      key: 'acciones_env',
      align: 'center',
      render: (_, fila) => (fila.activa ? (
        <Popconfirm
          title="¿Revocar la invitación?"
          description="Dejás de ver y gestionar los animales de esta cabaña al instante."
          okText="Revocar" cancelText="Cancelar" okButtonProps={{ danger: true }}
          onConfirm={() => revocarEnviada(fila)}
        >
          <Tooltip title="Revocar">
            <Button type="text" danger icon={<StopOutlined />} />
          </Tooltip>
        </Popconfirm>
      ) : <Text type="secondary">—</Text>),
    },
  ];

  const { columnas: colsRec, propsTabla: propsRec } = useColumnasResponsive({
    desktop: colsRecibidas,
    mobile: {
      title: 'Remate',
      principal: 'nombre_evento',
      secundarios: [
        { key: 'fechas' },
        { key: 'animales', etiqueta: 'Mis animales' },
        /* NO puede faltar en mobile. El hook descarta EN SILENCIO toda columna
           que no esté acá, y el motivo por el que esta columna existe —que la
           decisión sobre el acceso a tus propios animales no quede invisible—
           es exactamente el motivo por el que no puede desaparecer en un
           teléfono. */
        { key: 'cabanas_elegidas', etiqueta: 'Participo con' },
        { key: 'analytics' },
        { key: 'estado' },
      ],
      conservar: [{ key: 'acciones', width: 90 }],
    },
    scroll: { x: 'max-content' },
  });

  const { columnas: colsEnv, propsTabla: propsEnv } = useColumnasResponsive({
    desktop: colsEnviadas,
    mobile: {
      title: 'Cabaña',
      principal: 'invitada',
      secundarios: [
        { key: 'estado_env' },
        { key: 'cabanas', etiqueta: 'En el CSV' },
        { key: 'animales_env', etiqueta: 'En mis remates' },
      ],
      conservar: [{ key: 'acciones_env', width: 60 }],
    },
    scroll: { x: 'max-content' },
  });

  if (loading) return <PujaSpinner />;

  const solapas = [
    {
      key: 'recibidas',
      label: `Recibidas${recibidas.length ? ` (${recibidas.length})` : ''}`,
      children: recibidas.length === 0 ? (
        <Empty
          description={
            <Space direction="vertical" size={4}>
              <Text>Todavía no te invitaron a ningún remate</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Cuando una cabaña te invite a consignar, el remate aparece acá con
                sus fechas y tus animales.
              </Text>
            </Space>
          }
        />
      ) : (
        <Table {...propsRec} columns={colsRec} dataSource={recibidas}
               rowKey="id_evento_invitado" pagination={false} />
      ),
    },
  ];

  if (puedeInvitar) {
    solapas.push({
      key: 'enviadas',
      label: `Enviadas${enviadas.length ? ` (${enviadas.length})` : ''}`,
      children: enviadas.length === 0 ? (
        <Empty description="Todavía no invitaste a ninguna cabaña a tus remates" />
      ) : (
        <Table {...propsEnv} columns={colsEnv} dataSource={enviadas}
               rowKey="id_evento_invitado" pagination={false} />
      ),
    });
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <CalendarOutlined /> Invitaciones
          </Title>
          <Text type="secondary">
            Quién puede consignar en tus remates, y en cuáles consignás vos.
          </Text>
        </Col>
        {puedeInvitar && (
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={abrirModal}>
              Invitar cabaña
            </Button>
          </Col>
        )}
      </Row>

      <Card className="card-tabla">
        <Tabs items={solapas} />
      </Card>

      <Modal
        title="Invitar una cabaña a un remate"
        open={modalAbierto}
        onCancel={() => setModalAbierto(false)}
        onOk={invitar}
        okText="Invitar"
        okButtonProps={{ disabled: !encontrada, loading: invitando }}
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical">
          {/* SE BUSCA POR CUIT EXACTO Y NO HAY LISTADO, a propósito: un
              desplegable con todas las cabañas le mostraría a un cliente la
              cartera entera de Puja — quiénes son y cuántos son. */}
          <Form.Item
            name="cuit"
            label="CUIT de la cabaña"
            tooltip="Se busca por CUIT completo. Necesitás saber a quién invitás."
          >
            <Input
              placeholder="30-12345678-9"
              onPressEnter={buscarPorCuit}
              addonAfter={
                <Button type="text" size="small" icon={<SearchOutlined />}
                        loading={buscando} onClick={buscarPorCuit}>
                  Buscar
                </Button>
              }
            />
          </Form.Item>

          {errorBusqueda && (
            <Alert type="warning" showIcon message={errorBusqueda} style={{ marginBottom: 16 }} />
          )}

          {encontrada && (
            <>
              <Alert
                type="success" showIcon
                message={encontrada.razon_social}
                description="Elegí a qué remate la querés invitar."
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="id_evento"
                label="Remate"
                rules={[{ required: true, message: 'Elegí un remate' }]}
                extra={
                  'La invitación queda PENDIENTE hasta que esa cabaña la acepte: '
                  + 'hasta entonces no vas a ver ninguno de sus animales.'
                }
              >
                <Select
                  placeholder="Elegí el remate"
                  loading={cargandoRemates}
                  options={remates.map((r) => ({ value: r.id_evento, label: r.nombre_evento }))}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Aceptar: con qué unidades participo (sólo con más de una) y cómo se
          presenta cada una. Ver `abrirAceptar`. */}
      <Modal
        title="Aceptar la invitación"
        open={!!modalAceptar}
        onOk={confirmarAceptar}
        onCancel={() => setModalAceptar(null)}
        okText="Aceptar invitación"
        cancelText="Ahora no"
        confirmLoading={aceptando}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Qué estás aceptando"
          description={
            'La cabaña organizadora va a poder ver y gestionar los animales de las '
            + 'unidades que elijas. Lo que no elijas queda fuera del remate y sigue '
            + 'siendo sólo tuyo. Podés revocarlo cuando quieras.'
          }
        />
        {misCabanas.length > 1 && (<>
        <Text strong>¿Con qué cabañas participás?</Text>
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%', marginTop: 8 }}
          placeholder="Todas mis cabañas"
          value={cabanasElegidas}
          onChange={setCabanasElegidas}
          options={misCabanas.map((c) => ({
            value: c.id_cabana,
            label: c.alias ? `${c.nom_cabana} (${c.alias})` : c.nom_cabana,
          }))}
        />
        {/* DEJARLO VACÍO NO ES "NINGUNA", ES "TODAS", y hay que decirlo: un
            multi-select en blanco se lee natural como "no elegí nada", que acá
            significaría lo contrario de lo que hace. */}
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          {cabanasElegidas.length === 0
            ? 'Si no elegís ninguna, participás con TODAS tus cabañas.'
            : `Participás con ${cabanasElegidas.length} de tus ${misCabanas.length} cabañas. `
              + 'El resto queda fuera de este remate.'}
        </Paragraph>
        </>)}
        {camposCodigos}
      </Modal>

      {/* Los mismos campos, para cambiarlos después de haber aceptado. */}
      <Modal
        title="Cómo aparecen mis cabañas"
        open={!!modalCodigos}
        onOk={confirmarCodigos}
        onCancel={() => setModalCodigos(null)}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={guardandoCodigos}
      >
        {camposCodigos}
      </Modal>
    </div>
  );
};

export default Invitaciones;
